import { useState, useMemo } from "react";
import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  FileText,
  Plus,
  Building2,
  CheckCircle,
  Send,
  Eye,
  Loader2,
  Download,
  Pencil,
  Trash2,
  CreditCard,
  DollarSign,
  Mail,
  Link2,
  Copy,
  Undo2,
  RotateCcw,
} from "lucide-react";
import { useAdminUsers, useOrgContracts, useOrganizations } from "@/hooks/use-resources";
import InvoiceFilters, { type InvoiceFilterState } from "@/components/invoice-dashboard/InvoiceFilters";
import InvoiceDashboardSection from "@/components/invoice-dashboard/InvoiceDashboardSection";
import RefundInvoiceDialog from "@/components/invoices/RefundInvoiceDialog";
import RevertPaymentDialog from "@/components/invoices/RevertPaymentDialog";
import GenerateInvoiceDialog from "@/components/invoices/GenerateInvoiceDialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { generateInvoicePdf } from "@/lib/invoice-pdf";
import { netOwedLabel, statusBadge, statusLabel } from "@/lib/invoice-status";
import type { OrgBillingProfile, OwnerBillingProfile, OwnerInvoice } from "@shared/schema";

type InvoiceListItem = OwnerInvoice & { ownerName: string };

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${Math.abs(num).toFixed(2)}`;
}

// Only draft/approved invoices can be edited (matches updateOwnerInvoicePatch's server guard).
const INVOICE_EDITABLE_STATUSES = new Set(["draft", "approved"]);
const LINE_ITEM_CATEGORIES = [
  "cleaning", "intra_stay_cleaning", "maintenance", "check_in", "miscellaneous",
  "expense", "credit", "supplies", "repairs", "service", "reimbursement", "other",
];

export default function AdminInvoices() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("invoices");
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<number | null>(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [profileOwnerId, setProfileOwnerId] = useState<number | null>(null);

  const [dashFilters, setDashFilters] = useState<InvoiceFilterState>(() => {
    const now = new Date();
    return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0] ?? '', status: 'all', search: '' };
  });

  const [editNotes, setEditNotes] = useState("");
  const [editLineItems, setEditLineItems] = useState<Array<{ description: string; category: string; amount: string; date?: string }>>([]);
  // Per-line "edit this charge" popup opened from the invoice detail's Service Charges table.
  const [editingLine, setEditingLine] = useState<{ index: number; description: string; category: string; amount: string; date: string } | null>(null);

  const { data: adminUsers } = useAdminUsers();
  const owners = (adminUsers || []).filter((u: any) => u.role === "owner");

  const { data: orgContractsData } = useOrgContracts();
  const { data: organizationsData } = useOrganizations();
  // PM orgs this service org has an active contract with (as consumer)
  const billedToOrgOptions = ((orgContractsData?.asConsumer || []) as any[])
    .filter((c: any) => c.status === "active")
    .map((c: any) => {
      const org = (organizationsData || []).find((o: any) => o.id === c.providerOrgId);
      return org ? { id: org.id, name: org.name } : null;
    })
    .filter(Boolean) as { id: number; name: string }[];

  const { data: invoices, isLoading: invoicesLoading } = useQuery<InvoiceListItem[]>({
    queryKey: ["/api/admin/owner-invoices"],
  });

  // "All Invoices" table respects the shared search box (PM name / invoice number).
  const filteredInvoices = useMemo(() => {
    const q = dashFilters.search.trim().toLowerCase();
    if (!q) return invoices || [];
    return (invoices || []).filter((inv) =>
      (inv.ownerName || "").toLowerCase().includes(q) ||
      (inv.invoiceNumber || "").toLowerCase().includes(q)
    );
  }, [invoices, dashFilters.search]);

  const { data: billingProfiles, isLoading: profilesLoading } = useQuery<OwnerBillingProfile[]>({
    queryKey: ["/api/admin/owner-billing-profiles"],
  });

  const { data: orgProfile } = useQuery<OrgBillingProfile | null>({
    queryKey: ["/api/admin/org-billing-profile"],
  });

  const { data: orgSettings } = useQuery<{ logoUrl?: string | null }>({
    queryKey: ["/api/admin/org-settings"],
  });

  const { data: orgLogoData } = useQuery<{ dataUrl: string | null }>({
    queryKey: ["/api/admin/org-logo-data"],
    enabled: !!orgSettings?.logoUrl,
  });


  const { data: selectedProfile, isLoading: profileLoading } = useQuery<OwnerBillingProfile | null>({
    queryKey: ["/api/admin/owner-billing-profiles", profileOwnerId],
    enabled: !!profileOwnerId,
  });

  const [profileForm, setProfileForm] = useState({
    companyName: "",
    taxId: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    billingEmail: "",
    billingPhone: "",
    autoInvoicePeriod: null as string | null,
    autoInvoiceDayOfMonth: null as number | null,
    autoInvoiceDayOfWeek: null as number | null,
  });

  const approveMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/admin/owner-invoices/${id}/approve`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      toast({ title: "Invoice approved" });
    },
  });

  const issueMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/admin/owner-invoices/${id}/issue`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      toast({ title: "Invoice issued" });
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/admin/owner-invoices/${id}/paid`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to mark invoice as paid");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      toast({ title: "Invoice marked as paid" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: async ({ id, amount, method, notes }: { id: number; amount: string; method: string; notes?: string }) => {
      const res = await apiRequest("POST", `/api/admin/owner-invoices/${id}/record-payment`, {
        amount,
        paymentMethod: method,
        notes: notes || undefined,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to record payment");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      setShowPaymentDialog(false);
      setPaymentAmount("");
      setPaymentMethod("bank_transfer");
      setPaymentNotes("");
      const msg = data.status === "paid" ? "Invoice fully paid" : "Partial payment recorded";
      toast({ title: msg });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { notes?: string; lineItems?: Array<{ description: string; category: string; amount: string; date?: string }> } }) => {
      const res = await apiRequest("PATCH", `/api/admin/owner-invoices/${id}`, data);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      setShowEditDialog(false);
      toast({ title: "Invoice updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to update invoice", variant: "destructive" });
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/owner-invoices/${id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to delete invoice");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setDeleteInvoiceId(null);
      setSelectedInvoice(null);
      toast({ title: "Invoice deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Cannot delete invoice", description: err.message || "Failed to delete invoice", variant: "destructive" });
      setDeleteInvoiceId(null);
    },
  });

  const refundInvoiceMutation = useMutation({
    mutationFn: async ({ id, amount, reason }: { id: number; amount?: string; reason: string }) => {
      const res = await apiRequest("POST", `/api/admin/owner-invoices/${id}/refund`, {
        amount: amount || undefined,
        reason,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to refund invoice");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      setShowRefundDialog(false);
      toast({ title: "Invoice refunded" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const revertPaymentMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await apiRequest("POST", `/api/admin/owner-invoices/${id}/revert`, { reason });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to revert payment");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/finance-records"] });
      setSelectedInvoice(data);
      setShowRevertDialog(false);
      toast({ title: "Payment reverted", description: "Invoice is editable again and underlying charges are pending." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const emailInvoiceMutation = useMutation({
    mutationFn: async ({ id, email }: { id: number; email: string }) => {
      const res = await apiRequest("POST", `/api/admin/owner-invoices/${id}/email`, { email });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-invoices/receivables"] });
      setSelectedInvoice(data);
      setShowEmailDialog(false);
      toast({ title: "Invoice emailed", description: `Sent to ${data.sentTo}` });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to send email", variant: "destructive" });
    },
  });

  const saveProfileMutation = useMutation({
    mutationFn: async ({ ownerUserId, data }: { ownerUserId: number; data: typeof profileForm }) => {
      const res = await apiRequest("PUT", `/api/admin/owner-billing-profiles/${ownerUserId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/owner-billing-profiles"] });
      setShowProfileDialog(false);
      toast({ title: "Billing profile saved" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to save profile", variant: "destructive" });
    },
  });


  function openProfileEditor(ownerId: number) {
    setProfileOwnerId(ownerId);
    const existing = (billingProfiles || []).find((p: any) => p.ownerUserId === ownerId);
    if (existing) {
      setProfileForm({
        companyName: existing.companyName || "",
        taxId: existing.taxId || "",
        addressLine1: existing.addressLine1 || "",
        addressLine2: existing.addressLine2 || "",
        city: existing.city || "",
        state: existing.state || "",
        postalCode: existing.postalCode || "",
        country: existing.country || "",
        billingEmail: existing.billingEmail || "",
        billingPhone: existing.billingPhone || "",
        autoInvoicePeriod: (existing as any).autoInvoicePeriod ?? null,
        autoInvoiceDayOfMonth: (existing as any).autoInvoiceDayOfMonth ?? null,
        autoInvoiceDayOfWeek: (existing as any).autoInvoiceDayOfWeek ?? null,
      });
    } else {
      setProfileForm({ companyName: "", taxId: "", addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "", country: "", billingEmail: "", billingPhone: "", autoInvoicePeriod: null, autoInvoiceDayOfMonth: null, autoInvoiceDayOfWeek: null });
    }
    setShowProfileDialog(true);
  }

  function openEditDialog(invoice: any) {
    setEditNotes(invoice.notes || "");
    setEditLineItems((invoice.lineItems || []).map((item: any) => ({
      description: item.description || "",
      category: item.category || "misc",
      amount: String(item.amount || "0.00"),
      date: item.date || undefined,
    })));
    setShowEditDialog(true);
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Property Manager Invoices</h1>
            <p className="text-sm text-muted-foreground">Generate and manage invoices for property managers</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="button-create-invoice">
            <Plus className="h-4 w-4 mr-2" />
            Generate Invoice
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="billing-profiles" data-testid="tab-billing-profiles">Billing Profiles</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="space-y-4">
            <InvoiceFilters filters={dashFilters} onChange={setDashFilters} />
            <InvoiceDashboardSection
              filters={dashFilters}
              onSelectInvoiceForPayment={(id) => {
                const inv = (invoices || []).find((i: any) => i.id === id);
                if (inv) { setSelectedInvoice(inv); setShowPaymentDialog(true); }
              }}
              onSelectInvoiceForDetail={(id) => {
                const inv = (invoices || []).find((i: any) => i.id === id);
                if (inv) setSelectedInvoice(inv);
              }}
              onSelectInvoiceForEmail={(id) => {
                const inv = (invoices || []).find((i: any) => i.id === id);
                if (!inv) return;
                const owner = (owners || []).find((o: any) => o.id === inv.ownerUserId);
                const profile = (billingProfiles || []).find((p: any) => p.ownerUserId === inv.ownerUserId);
                setSelectedInvoice(inv);
                setEmailRecipient(profile?.billingEmail || (inv.companySnapshot as any)?.billingEmail || owner?.email || '');
                setShowEmailDialog(true);
              }}
            />
            <div className="text-sm font-medium text-muted-foreground pt-2">All Invoices</div>
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{dashFilters.search ? "No invoices match your search" : "No invoices generated yet"}</p>
                  {!dashFilters.search && <p className="text-sm text-muted-foreground mt-1">Click "Generate Invoice" to create one from report data</p>}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Property Manager</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Service Charges</TableHead>
                        <TableHead className="text-right">Net Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((inv: any) => {
                        const net = netOwedLabel(inv.netOwed, inv.paidAmount, inv.status);
                        return (
                          <TableRow key={inv.id} data-testid={`row-invoice-${inv.id}`}>
                            <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
                            <TableCell>{inv.ownerName}</TableCell>
                            <TableCell className="text-sm">
                              {formatDate(inv.periodStart)} - {formatDate(inv.periodEnd)}
                            </TableCell>
                            <TableCell className="text-right">{formatMoney(inv.subtotal)}</TableCell>
                            <TableCell className={`text-right font-medium ${net.className}`} data-testid={`text-net-${inv.id}`}>
                              {net.text}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const badge = statusBadge(inv.status);
                                return (
                                  <Badge variant={badge.variant} className={badge.className} data-testid={`badge-status-${inv.id}`}>
                                    {statusLabel(inv.status)}
                                  </Badge>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setSelectedInvoice(inv)}
                                  data-testid={`button-view-invoice-${inv.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={async () => {
                                    try {
                                      await generateInvoicePdf({ ...inv, orgLogoUrl: orgLogoData?.dataUrl ?? null }, orgProfile);
                                    } catch {
                                      toast({ title: "Error", description: "Failed to generate PDF.", variant: "destructive" });
                                    }
                                  }}
                                  data-testid={`button-pdf-invoice-${inv.id}`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedInvoice(inv);
                                    openEditDialog(inv);
                                  }}
                                  data-testid={`button-edit-invoice-${inv.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setDeleteInvoiceId(inv.id)}
                                  disabled={inv.status === "paid" || inv.status === "partially_paid"}
                                  title={
                                    inv.status === "paid" || inv.status === "partially_paid"
                                      ? "Refund the invoice before deleting"
                                      : "Delete invoice"
                                  }
                                  data-testid={`button-delete-invoice-${inv.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="billing-profiles" className="space-y-4">

            {profilesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Property Manager Billing Profiles</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property Manager</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Tax ID</TableHead>
                        <TableHead>Billing Email</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {owners.map((owner: any) => {
                        const profile = (billingProfiles || []).find((p: any) => p.ownerUserId === owner.id);
                        return (
                          <TableRow key={owner.id} data-testid={`row-owner-${owner.id}`}>
                            <TableCell className="font-medium">{owner.fullName}</TableCell>
                            <TableCell>{profile?.companyName || <span className="text-muted-foreground">Not set</span>}</TableCell>
                            <TableCell>{profile?.taxId || "-"}</TableCell>
                            <TableCell>{profile?.billingEmail || "-"}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openProfileEditor(owner.id)}
                                data-testid={`button-edit-profile-${owner.id}`}
                              >
                                <Building2 className="h-4 w-4 mr-1" />
                                {profile ? "Edit" : "Set Up"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {owners.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No property managers found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <GenerateInvoiceDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          owners={owners}
          billedToOrgOptions={billedToOrgOptions}
        />

        <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Property Manager Billing Profile</DialogTitle>
              <DialogDescription>
                Company information used on invoices
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={profileForm.companyName}
                  onChange={(e) => setProfileForm({ ...profileForm, companyName: e.target.value })}
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label>Tax ID / EIN</Label>
                <Input
                  value={profileForm.taxId}
                  onChange={(e) => setProfileForm({ ...profileForm, taxId: e.target.value })}
                  data-testid="input-tax-id"
                />
              </div>
              <div>
                <Label>Address Line 1</Label>
                <Input
                  value={profileForm.addressLine1}
                  onChange={(e) => setProfileForm({ ...profileForm, addressLine1: e.target.value })}
                  data-testid="input-address1"
                />
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input
                  value={profileForm.addressLine2}
                  onChange={(e) => setProfileForm({ ...profileForm, addressLine2: e.target.value })}
                  data-testid="input-address2"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={profileForm.city}
                    onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={profileForm.state}
                    onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                    data-testid="input-state"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    value={profileForm.postalCode}
                    onChange={(e) => setProfileForm({ ...profileForm, postalCode: e.target.value })}
                    data-testid="input-postal-code"
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={profileForm.country}
                    onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                    data-testid="input-country"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Billing Email</Label>
                  <Input
                    type="email"
                    value={profileForm.billingEmail}
                    onChange={(e) => setProfileForm({ ...profileForm, billingEmail: e.target.value })}
                    data-testid="input-billing-email"
                  />
                </div>
                <div>
                  <Label>Billing Phone</Label>
                  <Input
                    value={profileForm.billingPhone}
                    onChange={(e) => setProfileForm({ ...profileForm, billingPhone: e.target.value })}
                    data-testid="input-billing-phone"
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label className="text-sm font-medium">Auto-invoice Override</Label>
                <p className="text-xs text-muted-foreground">Leave blank to use org default.</p>
                <div className="flex gap-2 flex-wrap">
                  <Select
                    value={profileForm.autoInvoicePeriod || "org_default"}
                    onValueChange={v => setProfileForm({ ...profileForm, autoInvoicePeriod: v === "org_default" ? null : v })}
                  >
                    <SelectTrigger className="flex-1 h-8 text-sm min-w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org_default">Use org default</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                  {(profileForm.autoInvoicePeriod === "weekly" || profileForm.autoInvoicePeriod === "biweekly") && (
                    <Select
                      value={String(profileForm.autoInvoiceDayOfWeek ?? 1)}
                      onValueChange={v => setProfileForm({ ...profileForm, autoInvoiceDayOfWeek: Number(v) })}
                    >
                      <SelectTrigger className="w-32 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map((d, i) => (
                          <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {(profileForm.autoInvoicePeriod === "monthly" || profileForm.autoInvoicePeriod === "quarterly") && (
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      placeholder="Day 1-28"
                      className="w-24 h-8 text-sm"
                      value={profileForm.autoInvoiceDayOfMonth ?? ""}
                      onChange={e => setProfileForm({ ...profileForm, autoInvoiceDayOfMonth: e.target.value ? Number(e.target.value) : null })}
                    />
                  )}
                </div>
              </div>
              <Button
                className="w-full"
                disabled={!profileForm.companyName || saveProfileMutation.isPending}
                onClick={() => {
                  if (profileOwnerId) {
                    saveProfileMutation.mutate({ ownerUserId: profileOwnerId, data: profileForm });
                  }
                }}
                data-testid="button-save-profile"
              >
                {saveProfileMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Profile
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Invoice {selectedInvoice?.invoiceNumber}</DialogTitle>
              <DialogDescription>
                Update notes and line items for this invoice
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="resize-none"
                  data-testid="input-edit-notes"
                />
              </div>

              <div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <Label>Line Items</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditLineItems([...editLineItems, { description: "", category: "misc", amount: "0.00" }])}
                    data-testid="button-add-line-item"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                <div className="space-y-3">
                  {editLineItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-end">
                      <div className="flex-1">
                        {idx === 0 && <Label className="text-xs text-muted-foreground">Description</Label>}
                        <Input
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...editLineItems];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setEditLineItems(updated);
                          }}
                          data-testid={`input-line-desc-${idx}`}
                        />
                      </div>
                      <div className="w-28">
                        {idx === 0 && <Label className="text-xs text-muted-foreground">Amount</Label>}
                        <Input
                          value={item.amount}
                          onChange={(e) => {
                            const updated = [...editLineItems];
                            updated[idx] = { ...updated[idx], amount: e.target.value };
                            setEditLineItems(updated);
                          }}
                          data-testid={`input-line-amount-${idx}`}
                        />
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditLineItems(editLineItems.filter((_, i) => i !== idx))}
                        data-testid={`button-remove-line-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between text-sm font-medium pt-2 border-t">
                <span>New Subtotal</span>
                <span>{formatMoney(editLineItems.reduce((sum, item) => sum + Number(item.amount || 0), 0))}</span>
              </div>

              <Button
                className="w-full"
                disabled={updateInvoiceMutation.isPending}
                onClick={() => {
                  if (selectedInvoice) {
                    updateInvoiceMutation.mutate({
                      id: selectedInvoice.id,
                      data: {
                        notes: editNotes,
                        lineItems: editLineItems,
                      },
                    });
                  }
                }}
                data-testid="button-save-edit"
              >
                {updateInvoiceMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteInvoiceId} onOpenChange={(open) => { if (!open) setDeleteInvoiceId(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this invoice? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deleteInvoiceId) deleteInvoiceMutation.mutate(deleteInvoiceId);
                }}
                className="bg-destructive text-destructive-foreground"
                data-testid="button-confirm-delete"
              >
                {deleteInvoiceMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Per-charge edit popup — opened from the invoice detail's Service Charges table. */}
        <Dialog open={!!editingLine} onOpenChange={(open) => { if (!open) setEditingLine(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Charge</DialogTitle>
              <DialogDescription>Update this charge’s details. Saving recalculates the invoice total.</DialogDescription>
            </DialogHeader>
            {editingLine && (
              <div className="space-y-3">
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editingLine.description}
                    onChange={(e) => setEditingLine({ ...editingLine, description: e.target.value })}
                    data-testid="input-edit-line-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingLine.amount}
                      onChange={(e) => setEditingLine({ ...editingLine, amount: e.target.value })}
                      data-testid="input-edit-line-amount"
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={editingLine.date}
                      onChange={(e) => setEditingLine({ ...editingLine, date: e.target.value })}
                      data-testid="input-edit-line-date"
                    />
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editingLine.category} onValueChange={(v) => setEditingLine({ ...editingLine, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(LINE_ITEM_CATEGORIES.includes(editingLine.category)
                        ? LINE_ITEM_CATEGORIES
                        : [editingLine.category, ...LINE_ITEM_CATEGORIES]
                      ).map((c) => (
                        <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditingLine(null)}>Cancel</Button>
                  <Button
                    disabled={updateInvoiceMutation.isPending}
                    onClick={() => {
                      if (!selectedInvoice || !editingLine) return;
                      const current = (selectedInvoice.lineItems || []) as Array<{ description: string; category: string; amount: string; date?: string }>;
                      const nextLineItems = current.map((li, idx) => idx === editingLine.index
                        ? { description: editingLine.description, category: editingLine.category, amount: String(editingLine.amount || "0"), date: editingLine.date || undefined }
                        : li);
                      updateInvoiceMutation.mutate(
                        { id: selectedInvoice.id, data: { notes: selectedInvoice.notes ?? undefined, lineItems: nextLineItems } },
                        { onSuccess: () => setEditingLine(null) },
                      );
                    }}
                    data-testid="button-save-edit-line"
                  >
                    {updateInvoiceMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save Charge
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!selectedInvoice && !showEditDialog} onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invoice {selectedInvoice?.invoiceNumber}</DialogTitle>
              <DialogDescription>
                Invoice details and actions
              </DialogDescription>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Property Manager</p>
                    <p className="font-medium">{selectedInvoice.ownerName}</p>
                  </div>
                  {(() => {
                    const badge = statusBadge(selectedInvoice.status);
                    return (
                      <Badge variant={badge.variant} className={badge.className}>
                        {statusLabel(selectedInvoice.status)}
                      </Badge>
                    );
                  })()}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedInvoice.adminCompanySnapshot && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">From</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p className="font-medium">{selectedInvoice.adminCompanySnapshot.companyName}</p>
                        {selectedInvoice.adminCompanySnapshot.taxId && <p>Tax ID: {selectedInvoice.adminCompanySnapshot.taxId}</p>}
                        {selectedInvoice.adminCompanySnapshot.addressLine1 && <p>{selectedInvoice.adminCompanySnapshot.addressLine1}</p>}
                        {selectedInvoice.adminCompanySnapshot.addressLine2 && <p>{selectedInvoice.adminCompanySnapshot.addressLine2}</p>}
                        {(selectedInvoice.adminCompanySnapshot.city || selectedInvoice.adminCompanySnapshot.state) && (
                          <p>{[selectedInvoice.adminCompanySnapshot.city, selectedInvoice.adminCompanySnapshot.state, selectedInvoice.adminCompanySnapshot.postalCode].filter(Boolean).join(", ")}</p>
                        )}
                        {selectedInvoice.adminCompanySnapshot.billingEmail && <p>{selectedInvoice.adminCompanySnapshot.billingEmail}</p>}
                      </CardContent>
                    </Card>
                  )}
                  {selectedInvoice.companySnapshot && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Bill To</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p className="font-medium">{selectedInvoice.companySnapshot.companyName}</p>
                        {selectedInvoice.companySnapshot.taxId && <p>Tax ID: {selectedInvoice.companySnapshot.taxId}</p>}
                        {selectedInvoice.companySnapshot.addressLine1 && <p>{selectedInvoice.companySnapshot.addressLine1}</p>}
                        {selectedInvoice.companySnapshot.addressLine2 && <p>{selectedInvoice.companySnapshot.addressLine2}</p>}
                        {(selectedInvoice.companySnapshot.city || selectedInvoice.companySnapshot.state) && (
                          <p>{[selectedInvoice.companySnapshot.city, selectedInvoice.companySnapshot.state, selectedInvoice.companySnapshot.postalCode].filter(Boolean).join(", ")}</p>
                        )}
                        {selectedInvoice.companySnapshot.billingEmail && <p>{selectedInvoice.companySnapshot.billingEmail}</p>}
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Period</p>
                  <p className="text-sm">{formatDate(selectedInvoice.periodStart)} - {formatDate(selectedInvoice.periodEnd)}</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Service Charges</p>
                    {INVOICE_EDITABLE_STATUSES.has(selectedInvoice.status) && (
                      <span className="text-[11px] text-muted-foreground">Click a charge to edit</span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        {INVOICE_EDITABLE_STATUSES.has(selectedInvoice.status) && <TableHead className="w-8" />}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedInvoice.lineItems || []).map((item: any, i: number) => {
                        const editable = INVOICE_EDITABLE_STATUSES.has(selectedInvoice.status);
                        return (
                          <TableRow key={i}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.date ? formatDate(item.date) : '-'}</TableCell>
                            <TableCell className="text-right">{formatMoney(item.amount)}</TableCell>
                            {editable && (
                              <TableCell className="text-right">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => setEditingLine({
                                    index: i,
                                    description: item.description || "",
                                    category: item.category || "miscellaneous",
                                    amount: String(item.amount ?? "0"),
                                    date: item.date ? (new Date(item.date).toISOString().split("T")[0] ?? "") : "",
                                  })}
                                  data-testid={`button-edit-line-${i}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell className="font-medium" colSpan={2}>Total Service Charges</TableCell>
                        <TableCell className="text-right font-medium">{formatMoney(selectedInvoice.subtotal)}</TableCell>
                        {INVOICE_EDITABLE_STATUSES.has(selectedInvoice.status) && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">Cash Collected</span>
                    <span>{formatMoney(selectedInvoice.cashCollected)}</span>
                  </div>
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">Service Charges</span>
                    <span>-{formatMoney(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">Settlements</span>
                    <span>-{formatMoney(selectedInvoice.settlements)}</span>
                  </div>
                  <Separator />
                  {(() => {
                    const net = netOwedLabel(selectedInvoice.netOwed, selectedInvoice.paidAmount, selectedInvoice.status);
                    const num = Number(selectedInvoice.netOwed || 0);
                    return (
                      <div className="flex justify-between gap-2 font-medium">
                        <span>{num >= 0 ? "Net Owed by Property Manager" : "Net Owed to Property Manager"}</span>
                        <span className={net.className}>{formatMoney(selectedInvoice.netOwed)}</span>
                      </div>
                    );
                  })()}
                  {Number(selectedInvoice.paidAmount || 0) > 0 && (
                    <>
                      <div className="flex justify-between gap-2 text-sm">
                        <span className="text-muted-foreground">Amount Paid</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">{formatMoney(selectedInvoice.paidAmount)}</span>
                      </div>
                      <div className="flex justify-between gap-2 text-sm font-medium">
                        <span>Remaining Balance</span>
                        <span>{formatMoney(Math.abs(Number(selectedInvoice.netOwed || 0)) - Number(selectedInvoice.paidAmount || 0))}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{
                            width: `${Math.min(100, (Number(selectedInvoice.paidAmount || 0) / Math.max(0.01, Math.abs(Number(selectedInvoice.netOwed || 0)))) * 100)}%`,
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>

                {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                  <div>
                    <Separator className="mb-3" />
                    <p className="text-sm font-medium mb-2">Payment History</p>
                    <div className="space-y-2">
                      {selectedInvoice.payments.map((p: any) => (
                        <div key={p.id} className="flex justify-between items-start text-sm border rounded p-2">
                          <div>
                            <span className="font-medium">{formatMoney(p.amount)}</span>
                            <span className="text-muted-foreground ml-2 capitalize">{(p.paymentMethod || "").replace("_", " ")}</span>
                            {p.notes && <p className="text-xs text-muted-foreground mt-0.5">{p.notes}</p>}
                          </div>
                          <span className="text-muted-foreground text-xs whitespace-nowrap">{formatDate(p.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedInvoice.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedInvoice.notes}</p>
                  </div>
                )}

                {orgProfile && (orgProfile.bankName || orgProfile.zelleName || orgProfile.zelleEmail || orgProfile.zellePhone || orgProfile.venmoHandle || orgProfile.paypalEmail) && (
                  <div>
                    <Separator className="mb-4" />
                    <p className="text-sm font-medium flex items-center gap-1.5 mb-2" data-testid="text-payment-methods-title">
                      <CreditCard className="h-4 w-4" />
                      Payment Methods
                    </p>
                    <div className="text-sm space-y-1.5">
                      {orgProfile.bankName && (
                        <div data-testid="text-payment-bank">
                          <span className="text-muted-foreground">Bank Transfer: </span>
                          <span>{orgProfile.bankName}</span>
                          {orgProfile.bankAccountName && <span> - {orgProfile.bankAccountName}</span>}
                          {orgProfile.bankAccountNumber && (
                            <p className="text-muted-foreground ml-0 text-xs">Account: {orgProfile.bankAccountNumber}{orgProfile.bankRoutingNumber ? ` | Routing: ${orgProfile.bankRoutingNumber}` : ""}{orgProfile.bankSwiftCode ? ` | SWIFT: ${orgProfile.bankSwiftCode}` : ""}</p>
                          )}
                        </div>
                      )}
                      {(orgProfile.zelleName || orgProfile.zelleEmail || orgProfile.zellePhone) && (
                        <div data-testid="text-payment-zelle">
                          <span className="text-muted-foreground">Zelle: </span>
                          <span>{orgProfile.zelleName || ""}</span>
                          {orgProfile.zelleEmail && <span> ({orgProfile.zelleEmail})</span>}
                          {orgProfile.zellePhone && !orgProfile.zelleEmail && <span> ({orgProfile.zellePhone})</span>}
                        </div>
                      )}
                      {orgProfile.venmoHandle && (
                        <div data-testid="text-payment-venmo">
                          <span className="text-muted-foreground">Venmo: </span>
                          <span>{orgProfile.venmoHandle}</span>
                        </div>
                      )}
                      {orgProfile.paypalEmail && (
                        <div data-testid="text-payment-paypal">
                          <span className="text-muted-foreground">PayPal: </span>
                          <span>{orgProfile.paypalEmail}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end flex-wrap">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const token = (selectedInvoice as any).publicToken as string | null | undefined;
                        const payLinkUrl = token ? `${window.location.origin}/pay/${token}` : undefined;
                        await generateInvoicePdf({ ...selectedInvoice, orgLogoUrl: orgLogoData?.dataUrl ?? null, payLinkUrl }, orgProfile);
                      } catch {
                        toast({ title: "Error", description: "Failed to generate PDF. Please try again.", variant: "destructive" });
                      }
                    }}
                    data-testid="button-download-pdf"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const owner = owners.find((o: any) => o.id === selectedInvoice.ownerUserId);
                      const profile = (billingProfiles || []).find((p: any) => p.ownerUserId === selectedInvoice.ownerUserId);
                      setEmailRecipient(
                        profile?.billingEmail || (selectedInvoice.companySnapshot as any)?.billingEmail || owner?.email || ""
                      );
                      setShowEmailDialog(true);
                    }}
                    data-testid="button-email-invoice"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email Invoice
                  </Button>
                  {["issued", "sent", "partially_paid"].includes(selectedInvoice.status) && (selectedInvoice as any).publicToken && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const token = (selectedInvoice as any).publicToken as string;
                        const url = `${window.location.origin}/pay/${token}`;
                        navigator.clipboard.writeText(url).then(() => {
                          toast({ title: "Pay link copied", description: url });
                        }).catch(() => {
                          toast({ title: "Link", description: url });
                        });
                      }}
                      data-testid="button-copy-pay-link"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Copy Pay Link
                    </Button>
                  )}
                  {selectedInvoice.sentAt && (
                    <p className="text-xs text-muted-foreground">
                      Sent to {selectedInvoice.sentTo} on {formatDate(selectedInvoice.sentAt)}
                    </p>
                  )}
                  {!["issued", "paid", "partially_paid", "refunded"].includes(selectedInvoice.status) && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => openEditDialog(selectedInvoice)}
                        data-testid="button-edit-from-detail"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setDeleteInvoiceId(selectedInvoice.id)}
                        data-testid="button-delete-from-detail"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </>
                  )}
                  {selectedInvoice.status === "refunded" && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteInvoiceId(selectedInvoice.id)}
                      data-testid="button-delete-from-detail"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  {selectedInvoice.status === "draft" && (
                    <Button
                      onClick={() => approveMutation.mutate(selectedInvoice.id)}
                      disabled={approveMutation.isPending}
                      data-testid="button-approve-invoice"
                    >
                      {approveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Approve
                    </Button>
                  )}
                  {selectedInvoice.status === "approved" && (
                    <Button
                      onClick={() => issueMutation.mutate(selectedInvoice.id)}
                      disabled={issueMutation.isPending}
                      data-testid="button-issue-invoice"
                    >
                      {issueMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Issue to Property Manager
                    </Button>
                  )}
                  {["approved", "issued", "sent", "partially_paid"].includes(selectedInvoice.status) && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setPaymentAmount("");
                          setPaymentMethod("bank_transfer");
                          setPaymentNotes("");
                          setShowPaymentDialog(true);
                        }}
                        data-testid="button-record-payment"
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Record Payment
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => markPaidMutation.mutate(selectedInvoice.id)}
                        disabled={markPaidMutation.isPending}
                        data-testid="button-mark-paid"
                      >
                        {markPaidMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                        Mark Fully Paid
                      </Button>
                    </>
                  )}
                  {["paid", "partially_paid"].includes(selectedInvoice.status) && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setShowRevertDialog(true)}
                        data-testid="button-revert-payment"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Revert Payment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowRefundDialog(true)}
                        data-testid="button-refund-invoice"
                      >
                        <Undo2 className="h-4 w-4 mr-2" />
                        Refund
                      </Button>
                    </>
                  )}
                  {selectedInvoice.status === "refunded" && selectedInvoice.refundedAt && (
                    <p className="text-xs text-muted-foreground w-full">
                      Refunded {formatMoney(selectedInvoice.refundAmount)} on {formatDate(selectedInvoice.refundedAt)}
                      {selectedInvoice.refundReason ? ` — ${selectedInvoice.refundReason}` : ""}
                    </p>
                  )}
                  {selectedInvoice.revertedAt && selectedInvoice.status !== "refunded" && (
                    <p className="text-xs text-muted-foreground w-full">
                      Payment reverted on {formatDate(selectedInvoice.revertedAt)}
                      {selectedInvoice.revertReason ? ` — ${selectedInvoice.revertReason}` : ""}
                    </p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <RefundInvoiceDialog
          open={showRefundDialog}
          onOpenChange={setShowRefundDialog}
          paidAmount={selectedInvoice?.paidAmount}
          isPending={refundInvoiceMutation.isPending}
          onConfirm={({ amount, reason }) => {
            if (selectedInvoice) {
              refundInvoiceMutation.mutate({ id: selectedInvoice.id, amount, reason });
            }
          }}
        />
        <RevertPaymentDialog
          open={showRevertDialog}
          onOpenChange={setShowRevertDialog}
          paidAmount={selectedInvoice?.paidAmount}
          paidViaStripe={!!selectedInvoice?.stripePaymentSessionId}
          isPending={revertPaymentMutation.isPending}
          onConfirm={({ reason }) => {
            if (selectedInvoice) {
              revertPaymentMutation.mutate({ id: selectedInvoice.id, reason });
            }
          }}
        />
        <AlertDialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Record Payment</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedInvoice && (
                  <>
                    Outstanding: {formatMoney(Math.abs(Number(selectedInvoice.netOwed || 0)) - Number(selectedInvoice.paidAmount || 0))}
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="payment-amount">Amount ($)</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-payment-amount"
                />
              </div>
              <div>
                <Label htmlFor="payment-method">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger id="payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment-notes">Notes (optional)</Label>
                <Textarea
                  id="payment-notes"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Reference number, confirmation, etc."
                  data-testid="input-payment-notes"
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                onClick={() => {
                  if (selectedInvoice && paymentAmount && Number(paymentAmount) > 0) {
                    recordPaymentMutation.mutate({
                      id: selectedInvoice.id,
                      amount: paymentAmount,
                      method: paymentMethod,
                      notes: paymentNotes || undefined,
                    });
                  }
                }}
                disabled={!paymentAmount || Number(paymentAmount) <= 0 || recordPaymentMutation.isPending}
                data-testid="button-confirm-payment"
              >
                {recordPaymentMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />}
                Record Payment
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Email Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Send this invoice to the property manager by email.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label htmlFor="email-recipient">Recipient Email</Label>
              <Input
                id="email-recipient"
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="email@example.com"
                data-testid="input-email-recipient"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                onClick={() => {
                  if (selectedInvoice && emailRecipient) {
                    emailInvoiceMutation.mutate({ id: selectedInvoice.id, email: emailRecipient });
                  }
                }}
                disabled={!emailRecipient || emailInvoiceMutation.isPending}
                data-testid="button-confirm-send-email"
              >
                {emailInvoiceMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Send with PDF
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
